import { POST } from 'api';
import { ParagraphInterface } from 'entities/interfaces';

export const useChatProgress = (
  token: string,
  sessionIdRef?: React.MutableRefObject<string>,
  activeCourseIdRef?: React.MutableRefObject<string | null>
) => {
  const fetchNodeProgress = async (nodeId: number) => {
    const requestBody = new URLSearchParams();
    requestBody.append('userToken', token || '');
    requestBody.append('option', nodeId.toString());

    try {
      const response = await POST('/getInteractiveProgress', requestBody);

      if (response && response.progress) {
        return JSON.stringify({
          nodeId: response.option,
          progressState: response.progress.state,
          placedElements: response.progress.placedElements || {},
          points: response.progress.points || 0,
        });
      } else {
        console.error('No progress found or error occurred:', response.error);
        return null;
      }
    } catch (error) {
      console.error('Error fetching progress:', error);
      return null;
    }
  };

  const handleSaveProgress = async (
    nodesData: ParagraphInterface[],
    activeId: number,
    data: { placedElements?: Record<string, string[]>; selectedOptions?: string[]; points: number },
    updateTotalPoints: () => void
  ) => {
    const progressBody = new URLSearchParams();
    progressBody.append('userToken', token || '');

    if (nodesData && nodesData.length > 0) {
      const last = nodesData[nodesData.length - 1];
      progressBody.append('option', last.id.toString());
    } else {
      progressBody.append('option', "unknown");
    }

    const isSortTheParagraph = data.placedElements
      ? Object.values(data.placedElements).flat().every(id => typeof id === "string" && id.startsWith("step"))
      : false;

    const content = {
      courseId: activeId,
      type: isSortTheParagraph ? 'stp' : data.placedElements ? 'dnd' : 'mc',
      achievedAt: new Date().toISOString(),
      points: data.points,
      placedElements: data.placedElements || {},
      selectedOptions: data.selectedOptions || [],
    };

    try {
      progressBody.append('content', JSON.stringify(content));
    } catch (error) {
      console.error("Error parsing progress content:", error);
    }

    try {
      const response = await POST('setInteractiveProgress', progressBody);
      updateTotalPoints();
      if (response.status !== 200) {
        console.error("Progress saving failed:", response);
      }
    } catch (error) {
      console.error("Network error during progress save:", error);
    }
  };

  const loadChatProgress = async (
    nodeId: number,
    courseIdCheck: string,
    sessionId: string
  ): Promise<{ user_response: string; gpt_response: string } | null> => {
    if (!sessionIdRef || !activeCourseIdRef) {
      return null;
    }
    if (sessionIdRef.current !== sessionId) return null;

    const body = new URLSearchParams();
    body.append('userToken', token || '');
    body.append('nodeId', nodeId.toString());

    const cacheKey = `chatBackup_${sessionIdRef.current}_${nodeId}`;

    try {
      const response = await POST('getChatProgress', body);

      if (activeCourseIdRef.current !== courseIdCheck) return null;

      if (response && response.status === 200 && (response.result || response.chatHistory)) {
        const chatData = response.result || response.chatHistory;
        const { user_response, gpt_response } = chatData;

        return {
          user_response: user_response?.trim() || "[Keine Nutzereingabe vorhanden]",
          gpt_response: gpt_response?.trim() || "[Keine GPT-Antwort empfangen]",
        };
      } else {
        return null; 
      }
    } catch (error) {
      console.error("❌ Fehler beim Laden vom Server – versuche lokalen Cache:", error);
    }

    const backup = localStorage.getItem(cacheKey);
    if (backup) {
      const parsed = JSON.parse(backup);
      return {
        user_response: parsed.user_response || "[local user missing]",
        gpt_response: parsed.gpt_response || "[local gpt missing]",
      };
    }

    return null;
  };

  return {
    fetchNodeProgress,
    handleSaveProgress,
    loadChatProgress, // Exportiere hiermit
  };
};
